// Timeline pane — chronological list of events with severity flags,
// numbered-footnote sources (book-style), and per-row delete.
//
// Source attribution model:
//   Each distinct source the timeline references gets a number (1, 2, …)
//   the first time it appears. Subsequent uses of the same source reuse
//   the same number. A source is identified by (source_file_id) when
//   present, otherwise by (source, source_message_id), otherwise by the
//   originating event id (manual entries — which have no real "source"
//   beyond the user typing it).
//
//   Footnote numbers render inline at the end of the summary, like book
//   footnotes — "the deal was signed[1][3]". Hovering reveals the source
//   name + excerpt; clicking opens a modal with the full document or
//   chat turn.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { type EventProofState, useDismissedGaps, useEvidenceState } from '../evidence';
import { type CaseFile, useFileViewUrl, useFiles } from '../files';
import { m } from '../paraglide/messages';
import { type TimelineEvent, useTimeline } from '../timeline';
import { InconsistenciesBanner } from './inconsistencies-banner';

function typeLabel(t: TimelineEvent['event_type']): string {
  switch (t) {
    case 'agreement':
      return m.severityAgreement();
    case 'payment':
      return m.severityPayment();
    case 'work_day':
      return m.severityWorkDay();
    case 'missed_deadline':
      return m.severityMissedDeadline();
    case 'defect_discovery':
      return m.severityDefect();
    case 'complaint_sent':
      return m.severityComplaintSent();
    case 'contractor_response':
      return m.severityContractorResponse();
    case 'abandonment':
      return m.severityAbandonment();
    case 'inspection':
      return m.severityInspection();
    default:
      return m.severityOther();
  }
}

// Severity event types — these are the "something went wrong" markers that
// deserve a visible badge inline with the summary. Everything else (payment,
// agreement, work day, complaint, response, inspection, other) is plain
// timeline narration; date + summary tells you all you need.
const SEVERITY_TYPES = new Set<TimelineEvent['event_type']>([
  'defect_discovery',
  'abandonment',
  'missed_deadline',
]);

// ── Source registry ────────────────────────────────────────────────────────
// One entry per distinct source the timeline references. Numbered in the
// order each source first appears (so the same file always carries the
// same number across all events that cite it).

type SourceRef = {
  source: TimelineEvent['source'];
  source_file_id: string | null;
  source_message_id: string | null;
  source_excerpt: string | null;
  source_turn_ids: number[] | null;
  source_page_nos: number[] | null;
  source_turn_ts: string | null;
};

type SourceEntry = SourceRef & {
  number: number;
  /** Stable identity key. Used as the React key + Map key. */
  key: string;
};

/**
 * Each timeline-event ROW gets its own footnote, period.
 *
 * It's tempting to dedupe on (file_id, turn_no) — "same source turn,
 * same footnote." That's wrong. A single WhatsApp message can legitimately
 * back several distinct timeline events (a long "summary of this week"
 * message touches multiple topics: dust on equipment, scratches on the
 * floor, contractor delays — three events, all from turn 14, each with
 * its own focused excerpt). Collapsing them would force the tooltip to
 * pick one excerpt at random and hide the others — that's exactly the
 * bug this rule prevents.
 *
 * Cross-file corroboration (the "this fact is also in another file"
 * case) is handled by the dedup pass: it persists each duplicate as a
 * distinct row with its own id, so each gets its own footnote too —
 * sourced from a different file, rendered as adjacent superscripts on
 * the canonical event.
 */
function refKey(_r: SourceRef, eventOrDuplicateRowId: string): string {
  return `evt:${eventOrDuplicateRowId}`;
}

function eventToRef(e: TimelineEvent): SourceRef {
  return {
    source: e.source,
    source_file_id: e.source_file_id,
    source_message_id: e.source_message_id,
    source_excerpt: e.source_excerpt,
    source_turn_ids: e.source_turn_ids,
    source_page_nos: e.source_page_nos,
    source_turn_ts: e.source_turn_ts ?? null,
  };
}

/**
 * Walk every event (canonical + corroborating duplicates) and build a
 * `key → {number, ref}` registry. Stable across re-renders because we
 * derive it from the events list with useMemo.
 */
function buildSourceRegistry(events: TimelineEvent[]): {
  byKey: Map<string, SourceEntry>;
  // Per-event ordered list of source-keys to render as footnote numbers.
  perEvent: Map<string, string[]>;
} {
  const byKey = new Map<string, SourceEntry>();
  const perEvent = new Map<string, string[]>();
  let nextNumber = 1;

  // Iterate in render order (oldest → newest? No — events come newest-first
  // from the API. We want number 1 = the first source the *user* sees as
  // they scan top-to-bottom, which is the newest event's primary source.
  // Numbering in array order matches reading order.) Same source seen
  // twice keeps its first-assigned number.
  for (const e of events) {
    // Each entry tracks its ref + the event-id we should fall back to
    // when the ref has no file_id / message_id (manual entries). Index 0
    // is the canonical event itself; the rest are the merged duplicates.
    const refsWithFallback: Array<{ ref: SourceRef; fallbackId: string }> = [
      { ref: eventToRef(e), fallbackId: e.id },
      ...(e.corroborating_sources ?? []).map((s) => ({
        ref: {
          source: s.source,
          source_file_id: s.source_file_id,
          source_message_id: s.source_message_id,
          source_excerpt: s.source_excerpt,
          source_turn_ids: s.source_turn_ids,
          source_page_nos: s.source_page_nos,
          source_turn_ts: s.source_turn_ts ?? null,
        } as SourceRef,
        fallbackId: s.id,
      })),
    ];
    const eventKeys: string[] = [];
    for (const { ref, fallbackId } of refsWithFallback) {
      const key = refKey(ref, fallbackId);
      if (!byKey.has(key)) {
        byKey.set(key, { ...ref, number: nextNumber++, key });
      }
      eventKeys.push(key);
    }
    perEvent.set(e.id, eventKeys);
  }
  return { byKey, perEvent };
}

// ── Date grouping ──────────────────────────────────────────────────────────

type DayGroup = { date: string; events: TimelineEvent[] };

function groupByDate(events: TimelineEvent[]): DayGroup[] {
  const groups: DayGroup[] = [];
  let current: DayGroup | null = null;
  for (const e of events) {
    if (!current || current.date !== e.event_date) {
      current = { date: e.event_date, events: [] };
      groups.push(current);
    }
    current.events.push(e);
  }
  return groups;
}

// ── Pane ──────────────────────────────────────────────────────────────────

// Evidence-gap dismissal hook lives in `../evidence` so the artifacts pane
// can subscribe to the same state — see useDismissedGaps there.

export function TimelinePane({ caseId }: { caseId: string }) {
  const tl = useTimeline(caseId);
  const filesQ = useFiles(caseId);
  const evidenceQ = useEvidenceState(caseId);
  const [openSourceKey, setOpenSourceKey] = useState<string | null>(null);
  const [flashedIds, setFlashedIds] = useState<Set<string>>(() => new Set());
  const { dismissed: dismissedGaps, dismiss: dismissGap } = useDismissedGaps(caseId);

  // Hooks must run on every render — derive registry/files map up front,
  // even when the underlying data is empty. Early returns happen below.
  const events = tl.data ?? [];
  const registry = useMemo(() => buildSourceRegistry(events), [events]);
  const filesById = useMemo(() => {
    const map = new Map<string, CaseFile>();
    for (const f of filesQ.data ?? []) map.set(f.id, f);
    return map;
  }, [filesQ.data]);

  // Map every timeline-event id → its footnote number, so the
  // inconsistencies banner can render numbered chips that match the
  // footnotes the user sees inline. We pick the first source's number
  // for each event (events typically have one footnote each).
  const footnoteByEventId = useMemo(() => {
    const map = new Map<string, number>();
    for (const [eid, keys] of registry.perEvent.entries()) {
      const firstKey = keys[0];
      const entry = firstKey ? registry.byKey.get(firstKey) : undefined;
      if (entry) map.set(eid, entry.number);
    }
    return map;
  }, [registry]);

  // "Show evidence" handler — scrolls the conflicting events into view
  // and pulse-highlights them. We use a single one-shot Set so the
  // animation runs once per click and self-clears.
  const showEvidence = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setFlashedIds(new Set(ids));
    // Best-effort scroll to the first event.
    requestAnimationFrame(() => {
      const first = ids[0];
      if (!first) return;
      const el = document.querySelector(
        `[data-timeline-event-id="${CSS.escape(first)}"]`,
      ) as HTMLElement | null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    // Clear the flashed set after the CSS animation finishes.
    window.setTimeout(() => setFlashedIds(new Set()), 2400);
  }, []);

  // Listen for `claimpack:reveal-target` with kind='evidence' —
  // dispatched when the user clicks "Attach a document to N timeline
  // events →" on the artifacts pack card. We pull every proof-missing
  // event id from the evidence state and reuse `showEvidence` to flash
  // them all (scrolling to the first). Without this the click would
  // silently switch tabs and leave the user staring at the timeline
  // wondering which events the pack card meant.
  useEffect(() => {
    function onReveal(e: Event) {
      const detail = (e as CustomEvent<{ kind?: string }>).detail;
      if (!detail || detail.kind !== 'evidence') return;
      const states = evidenceQ.data?.states ?? [];
      const ids = states.filter((s) => s.status === 'proof-missing').map((s) => s.eventId);
      showEvidence(ids);
    }
    window.addEventListener('claimpack:reveal-target', onReveal);
    return () => window.removeEventListener('claimpack:reveal-target', onReveal);
  }, [evidenceQ.data, showEvidence]);

  if (tl.isLoading) {
    return (
      <div className="timeline-empty">
        <p className="muted">{m.timelineLoadingMsg()}</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="timeline-empty">
        <p className="muted">{m.timelineEmpty()}</p>
        <p className="small muted">{m.timelineEmptyHint()}</p>
      </div>
    );
  }

  const groups = groupByDate(events);
  const openSource = openSourceKey ? (registry.byKey.get(openSourceKey) ?? null) : null;

  return (
    <>
      <div className="timeline-scroll">
        <InconsistenciesBanner
          caseId={caseId}
          onShowEvidence={showEvidence}
          footnoteByEventId={footnoteByEventId}
        />
        <p className="muted small timeline-edit-hint">
          Need to fix or remove an event? Tell the assistant in chat — it'll edit the timeline for
          you.
        </p>
        <ol className="timeline timeline-grouped">
          {groups.map((g) => (
            <li key={g.date} className="timeline-day">
              <time className="timeline-day-date">{g.date}</time>
              <ul className="timeline-day-events">
                {g.events.map((e) => (
                  <TimelineRow
                    key={e.id}
                    event={e}
                    sourceKeys={registry.perEvent.get(e.id) ?? []}
                    registry={registry.byKey}
                    filesById={filesById}
                    onOpenSource={(key) => setOpenSourceKey(key)}
                    flashed={flashedIds.has(e.id)}
                    proofState={evidenceQ.data?.byEvent.get(e.id) ?? null}
                    gapDismissed={dismissedGaps.has(e.id)}
                    onDismissGap={() => dismissGap(e.id)}
                  />
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </div>
      {openSource ? (
        <SourceModal
          caseId={caseId}
          source={openSource}
          file={
            openSource.source_file_id ? (filesById.get(openSource.source_file_id) ?? null) : null
          }
          onClose={() => setOpenSourceKey(null)}
        />
      ) : null}
    </>
  );
}

// ── Row ────────────────────────────────────────────────────────────────────

function TimelineRow({
  event: e,
  sourceKeys,
  registry,
  filesById,
  onOpenSource,
  flashed,
  proofState,
  gapDismissed,
  onDismissGap,
}: {
  event: TimelineEvent;
  sourceKeys: string[];
  registry: Map<string, SourceEntry>;
  filesById: Map<string, CaseFile>;
  onOpenSource: (key: string) => void;
  flashed: boolean;
  proofState: EventProofState | null;
  gapDismissed: boolean;
  onDismissGap: () => void;
}) {
  const isSeverity = SEVERITY_TYPES.has(e.event_type);

  return (
    <li className={`timeline-event${flashed ? ' is-flashed' : ''}`} data-timeline-event-id={e.id}>
      <p className="timeline-summary">
        {isSeverity ? (
          <span className={`severity-flag type-${e.event_type}`}>{typeLabel(e.event_type)}</span>
        ) : null}
        {e.summary}
        {sourceKeys.length > 0 ? (
          <span className="footnotes">
            {sourceKeys.map((k) => {
              const entry = registry.get(k);
              if (!entry) return null;
              return (
                <Footnote
                  key={k}
                  entry={entry}
                  file={entry.source_file_id ? (filesById.get(entry.source_file_id) ?? null) : null}
                  onOpen={() => onOpenSource(k)}
                />
              );
            })}
          </span>
        ) : null}
      </p>
      {proofState && proofState.status === 'proof-missing' && !gapDismissed ? (
        <EvidenceGapInline state={proofState} onDismiss={onDismissGap} />
      ) : null}
    </li>
  );
}

/**
 * Per-event inline gap row. Renders directly under the event summary
 * when the event is `proof-missing`. Replaces the old top-of-timeline
 * banner — gaps now sit *with* the event they belong to, so the user
 * can see "this specific payment needs a receipt" instead of skimming
 * a sticky list of 14 disembodied dates.
 *
 * Action: switches to the Sources tab via the same custom event the
 * tab strip already listens for.
 */
function categoryLabelFor(category: string): string {
  switch (category) {
    case 'invoice':
      return m.fileCategoryInvoice();
    case 'receipt':
      return m.fileCategoryReceipt();
    case 'photo':
      return m.fileCategoryPhoto();
    case 'contract':
      return m.fileCategoryContract();
    case 'email':
      return m.fileCategoryEmail();
    case 'whatsapp_export':
      return m.fileCategoryWhatsAppExport();
    case 'other':
      return m.fileCategoryOther();
    default:
      return category;
  }
}

// Quiet inline hint. The previous design rendered every proof-missing
// event inside a beige bordered card with imperative copy ("Add the
// matching X to back this up") — visually identical to an error, and
// when nine events stacked the page read as a list of failures rather
// than a list of facts. The new shape is a single muted row: paperclip
// icon, soft text framing the upload as a *strengthening* action (not a
// fix), and a small dismiss × for users who don't want the prompt on
// this specific row. No box, no background. Click anywhere on the
// row's link jumps to Sources via the same custom event the tab strip
// already listens for.
function EvidenceGapInline({
  state,
  onDismiss,
}: {
  state: EventProofState;
  onDismiss: () => void;
}) {
  const suggested = state.requirement.suggestedDocCategories;
  const labels = suggested.map(categoryLabelFor);
  const list =
    labels.length === 0
      ? null
      : labels.length === 1
        ? labels[0]
        : labels.length === 2
          ? `${labels[0]} ${m.evidenceInlineOr()} ${labels[1]}`
          : `${labels.slice(0, -1).join(', ')} ${m.evidenceInlineOr()} ${labels[labels.length - 1]}`;

  function jumpToSources() {
    window.dispatchEvent(new CustomEvent('claimpack:switch-tab', { detail: { tab: 'sources' } }));
  }

  return (
    <div className="timeline-event-gap" role="note">
      <span className="timeline-event-gap-icon" aria-hidden="true">
        📎
      </span>
      <button type="button" className="timeline-event-gap-link" onClick={jumpToSources}>
        {list ? m.evidenceInlineWithSuggestion({ list }) : m.evidenceInlineWithoutSuggestion()}
      </button>
      <button
        type="button"
        className="timeline-event-gap-dismiss"
        onClick={onDismiss}
        title={m.evidenceInlineDismissTitle()}
        aria-label={m.evidenceInlineDismissTitle()}
      >
        ✕
      </button>
    </div>
  );
}

// ── Footnote ──────────────────────────────────────────────────────────────

function sourceLabelFor(entry: SourceEntry, file: CaseFile | null): string {
  if (entry.source_file_id && file) return file.filename;
  switch (entry.source) {
    case 'whatsapp':
      return m.sourceWhatsApp();
    case 'document':
      return m.sourceDocument();
    case 'chat':
      return m.sourceChat();
    case 'manual':
      return m.sourceManual();
  }
}

/**
 * Format the source's timestamp as "Apr 11, 2024 · 14:32" — locale-aware
 * date + 24h time. Used for WhatsApp citations so the tooltip says when
 * the message was sent, which is how the user actually thinks about it.
 */
function formatTurnTs(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${date} · ${time}`;
}

function locatorFor(entry: SourceEntry): string | null {
  // Reader-friendly locators only.
  //   - PDF pages — "p. 3" mirrors how lawyers cite exhibits.
  //   - WhatsApp message — show the timestamp the message was sent at.
  //     The user thinks in "what time did João say that?", not in
  //     1-based turn indices.
  if (entry.source_page_nos && entry.source_page_nos.length > 0) {
    return `p. ${entry.source_page_nos.join(', ')}`;
  }
  if (entry.source_turn_ts) {
    return `sent ${formatTurnTs(entry.source_turn_ts)}`;
  }
  return null;
}

function Footnote({
  entry,
  file,
  onOpen,
}: {
  entry: SourceEntry;
  file: CaseFile | null;
  onOpen: () => void;
}) {
  const label = sourceLabelFor(entry, file);
  const locator = locatorFor(entry);
  return (
    <button
      type="button"
      className="footnote-ref"
      onClick={onOpen}
      aria-label={`Source ${entry.number}: ${label}`}
    >
      <sup>{entry.number}</sup>
      <span className="footnote-tooltip" role="tooltip">
        <span className="footnote-tooltip-label">
          {label}
          {locator ? <span className="footnote-tooltip-locator"> · {locator}</span> : null}
        </span>
        {entry.source_excerpt ? (
          <span className="footnote-tooltip-excerpt">"{entry.source_excerpt}"</span>
        ) : null}
        <span className="footnote-tooltip-hint">Click to view source</span>
      </span>
    </button>
  );
}

// ── Source modal ───────────────────────────────────────────────────────────
// Opens when the user clicks a footnote number. Renders the underlying
// document inline (PDF/image) or, for chat/manual sources, just the
// excerpt — no ambient document to embed.

function SourceModal({
  caseId,
  source,
  file,
  onClose,
}: {
  caseId: string;
  source: SourceEntry;
  file: CaseFile | null;
  onClose: () => void;
}) {
  // Esc to close — tiny QoL, big impact.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fileId = source.source_file_id;
  const viewUrl = useFileViewUrl(caseId, fileId);
  const label = sourceLabelFor(source, file);
  const locator = locatorFor(source);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Esc handling is wired via window listener
    <dialog
      className="modal-backdrop"
      open
      aria-label={`Source ${source.number}`}
      onClick={(e) => {
        // Click outside the inner shell dismisses; clicks on the shell
        // are confined because target !== currentTarget for those.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-shell source-modal">
        <header className="source-modal-head">
          <div className="source-modal-title">
            <span className="footnote-num">[{source.number}]</span>
            <span className="source-modal-name">{label}</span>
            {locator ? <span className="source-modal-locator">{locator}</span> : null}
          </div>
          <button type="button" className="btn-ghost btn-xs" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        {source.source_excerpt ? (
          <blockquote className="source-modal-excerpt">"{source.source_excerpt}"</blockquote>
        ) : null}
        <div className="source-modal-body">
          {fileId ? (
            viewUrl.isLoading ? (
              <p className="muted">Loading source…</p>
            ) : viewUrl.isError ? (
              <p className="notice error">Couldn't load the source file.</p>
            ) : viewUrl.data ? (
              <FilePreview
                url={viewUrl.data.url}
                contentType={viewUrl.data.contentType}
                filename={viewUrl.data.filename}
              />
            ) : null
          ) : source.source === 'chat' ? (
            <p className="muted small">
              This event came from your chat. The excerpt above is the verbatim line.
            </p>
          ) : source.source === 'manual' ? (
            <p className="muted small">
              You added this event by hand. There is no source document.
            </p>
          ) : (
            <p className="muted small">No source document is attached to this event.</p>
          )}
        </div>
      </div>
    </dialog>
  );
}

function FilePreview({
  url,
  contentType,
  filename,
}: {
  url: string;
  contentType: string;
  filename: string;
}) {
  if (contentType.startsWith('image/')) {
    return <img src={url} alt={filename} className="source-modal-image" />;
  }
  if (contentType === 'application/pdf') {
    return <iframe src={url} title={filename} className="source-modal-frame" />;
  }
  if (contentType.startsWith('text/') || filename.toLowerCase().endsWith('.txt')) {
    return (
      <iframe src={url} title={filename} className="source-modal-frame source-modal-frame-text" />
    );
  }
  // Unknown — fall back to a download link.
  return (
    <p className="muted small">
      <a href={url} target="_blank" rel="noopener noreferrer">
        Download {filename}
      </a>{' '}
      to view it (the browser doesn't preview this type inline).
    </p>
  );
}
